/**
 * On-device image pipeline for chat: decode → downscale → JPEG re-encode →
 * thumbnail. All of it on the client, none of it near Everhall API.
 *
 * The re-encode is the point, not an optimisation: drawing to a canvas and
 * re-exporting STRIPS EXIF — including the GPS coordinates a child's phone
 * stamps into a photo of their bedroom. Sharing raw camera files between
 * children is a location-leak we refuse by construction.
 *
 * Every step is OOM-guarded. Camera photos are 8-12 megapixels; allocating a
 * full-resolution canvas for one on a 2 GB Android is a multi-second hitch or a
 * tab crash. So: a hard input-dimension ceiling before any draw, a stepwise
 * halving downscale (each intermediate canvas is at most 4× the last, never the
 * 12MP original scaled in one go), and a wall-clock budget that fails soft.
 */

/** Above this in either dimension, refuse before decoding — a 20MP panorama is not a chat image. */
const MAX_INPUT_DIMENSION = 8192
/** Refuse a source file larger than this outright (before decode work). */
const MAX_INPUT_BYTES = 20 * 1024 * 1024
/** The longest edge of the shared image. Matches the server's MAX_DIMENSION. */
const MAX_OUTPUT_DIMENSION = 1600
const FULL_QUALITY = 0.8
/** The longest edge of the in-list thumbnail — small, decodes cheap, ~24 KB. */
const THUMB_DIMENSION = 320
const THUMB_QUALITY = 0.7
/** Whole pipeline budget; past this we fail with a message rather than hang. */
const BUDGET_MS = 5_000

export interface ProcessedImage {
  full: Blob
  thumb: Blob
  width: number
  height: number
  name: string
}

export type ImageError = 'notImage' | 'tooLarge' | 'decodeFailed' | 'timedOut' | 'animated'

export class ImagePipelineError extends Error {
  readonly kind: ImageError
  constructor(kind: ImageError) {
    super(kind)
    this.kind = kind
    this.name = 'ImagePipelineError'
  }
}

const ACCEPTED = /^image\/(jpeg|png|webp|heic|heif)$/i
/**
 * Formats that carry animation we would destroy. They are refused BY NAME so
 * the person gets "animated pictures can't be sent" instead of the old
 * "that is not a picture" — which was what a teacher saw after picking a GIF the
 * file dialog had just offered them, and is the least helpful true sentence
 * available. Silently sending frame one would be worse: the joke, the worked
 * example, the waving hello are all in the frames we dropped.
 */
const ANIMATED = /^image\/(gif|apng)$/i

// The picker's accept attribute moved to attachment.ts (ACCEPT_FILES_ATTRIBUTE):
// chat accepts images AND allowlisted non-image files, so the one contract the
// picker/drop/paste share spans both modules and lives with the allowlist.

/** Does this file stand a chance? Cheap, synchronous, no decode. */
export function isSupportedImage(file: File): boolean {
  return ACCEPTED.test(file.type)
}

/** A file we should name specifically rather than lump into "not a picture". */
export function isAnimatedImage(file: File): boolean {
  return ANIMATED.test(file.type)
}

export async function processImage(file: File): Promise<ProcessedImage> {
  if (ANIMATED.test(file.type)) throw new ImagePipelineError('animated')
  if (!ACCEPTED.test(file.type)) throw new ImagePipelineError('notImage')
  if (file.size > MAX_INPUT_BYTES) throw new ImagePipelineError('tooLarge')
  // WebP is the one accepted format that can be animated, and it cannot be told
  // apart by MIME — an animated .webp and a still one are both `image/webp`. It
  // has to be read out of the header, because the alternative is the silent
  // flattening we refuse GIFs to avoid, arriving through a different door.
  if (/^image\/webp$/i.test(file.type) && (await isAnimatedWebp(file))) {
    throw new ImagePipelineError('animated')
  }

  const deadline = performance.now() + BUDGET_MS
  const bitmap = await decode(file)
  try {
    if (bitmap.width > MAX_INPUT_DIMENSION || bitmap.height > MAX_INPUT_DIMENSION) {
      throw new ImagePipelineError('tooLarge')
    }
    if (performance.now() > deadline) throw new ImagePipelineError('timedOut')

    const fullCanvas = downscale(bitmap, MAX_OUTPUT_DIMENSION)
    const full = await toJpeg(fullCanvas, FULL_QUALITY)
    if (performance.now() > deadline) throw new ImagePipelineError('timedOut')

    // Thumbnail from the already-downscaled canvas, not the original — cheaper,
    // and it never touches the 12MP source a second time.
    const thumbCanvas = downscale(fullCanvas, THUMB_DIMENSION)
    const thumb = await toJpeg(thumbCanvas, THUMB_QUALITY)

    const width = fullCanvas.width
    const height = fullCanvas.height
    // Release the intermediate GPU canvas backing store on Android now that both
    // outputs are encoded — 0×0 canvases are collected far more eagerly.
    fullCanvas.width = 0
    fullCanvas.height = 0
    thumbCanvas.width = 0
    thumbCanvas.height = 0

    return { full, thumb, width, height, name: sanitizeName(file.name) }
  } finally {
    // Free the decoded bitmap promptly; on a low-end phone it is the biggest
    // allocation in flight.
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close()
  }
}

/**
 * Is this WebP animated?
 *
 * Only the extended format can be. Its layout is fixed and tiny: 'RIFF', four
 * size bytes, 'WEBP', then the first chunk's fourcc at byte 12. When that chunk
 * is 'VP8X' the feature flags byte sits at 20, and bit 1 is ANIM. A plain
 * 'VP8 '/'VP8L' file has no animation chunk at all and needs no further reading.
 */
async function isAnimatedWebp(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 21).arrayBuffer())
    if (head.length < 21) return false
    const fourcc = String.fromCharCode(head[12], head[13], head[14], head[15])
    if (fourcc !== 'VP8X') return false
    return (head[20] & 0x02) !== 0
  } catch {
    // A header we cannot read is not a reason to refuse the picture; the decode
    // below is the real gate.
    return false
  }
}

type Decoded = ImageBitmap | HTMLImageElement

/**
 * Reads intrinsic dimensions from the file HEADER without decoding the pixels —
 * so we can refuse an oversized image and decode-to-size, never allocating the
 * full 12 MP RGBA buffer (~48 MB) that a plain createImageBitmap(file) would.
 * Covers the raster formats a phone camera produces; returns null for anything
 * else (HEIC, exotic) so the caller falls back to a full decode.
 */
async function readDimensions(file: File): Promise<{ w: number; h: number } | null> {
  const head = new Uint8Array(await file.slice(0, 64).arrayBuffer())
  const view = new DataView(head.buffer)
  // PNG: 8-byte sig, then IHDR width/height as big-endian uint32 at 16/20.
  if (head[0] === 0x89 && head[1] === 0x50) {
    return { w: view.getUint32(16), h: view.getUint32(20) }
  }
  // WebP: 'RIFF'....'WEBP' then a chunk fourcc.
  if (head[0] === 0x52 && head[8] === 0x57 && head[9] === 0x45) {
    const fourcc = String.fromCharCode(head[12], head[13], head[14], head[15])
    if (fourcc === 'VP8 ')
      return { w: view.getUint16(26, true) & 0x3fff, h: view.getUint16(28, true) & 0x3fff }
    if (fourcc === 'VP8L') {
      const b = view.getUint32(21, true)
      return { w: (b & 0x3fff) + 1, h: ((b >> 14) & 0x3fff) + 1 }
    }
    if (fourcc === 'VP8X') {
      return {
        w: ((head[24] | (head[25] << 8) | (head[26] << 16)) & 0xffffff) + 1,
        h: ((head[27] | (head[28] << 8) | (head[29] << 16)) & 0xffffff) + 1,
      }
    }
    return null
  }
  // JPEG: walk the marker segments for the first Start-Of-Frame.
  if (head[0] === 0xff && head[1] === 0xd8) return readJpegDimensions(file)
  return null
}

async function readJpegDimensions(file: File): Promise<{ w: number; h: number } | null> {
  // The SOF marker can sit after a large EXIF/ICC segment, so scan a generous
  // prefix rather than the 64-byte head.
  const buf = new Uint8Array(await file.slice(0, 256 * 1024).arrayBuffer())
  let offset = 2
  while (offset + 9 < buf.length) {
    if (buf[offset] !== 0xff) {
      offset += 1
      continue
    }
    const marker = buf[offset + 1]
    // SOF0-SOF15 carry the frame dimensions; skip the arithmetic/DHT/etc ones.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      const h = (buf[offset + 5] << 8) | buf[offset + 6]
      const w = (buf[offset + 7] << 8) | buf[offset + 8]
      return { w, h }
    }
    if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset += 2
      continue
    }
    const segmentLength = (buf[offset + 2] << 8) | buf[offset + 3]
    if (segmentLength < 2) return null
    offset += 2 + segmentLength
  }
  return null
}

async function decode(file: File): Promise<Decoded> {
  // createImageBitmap is the fast path (off the main thread where supported)
  // and the one that lets us close() the allocation. Fall back to an <img> for
  // browsers/formats it refuses.
  if (typeof createImageBitmap === 'function') {
    // Decode-to-size: read the header dims, and if the source is bigger than our
    // output, ask the browser to resize DURING decode so the full-resolution
    // RGBA buffer is never allocated. On a 2 GB phone a 12 MP camera photo would
    // otherwise peak ~48 MB before any downscale and could crash the tab.
    const dims = await readDimensions(file).catch(() => null)
    if (dims) {
      if (dims.w > MAX_INPUT_DIMENSION || dims.h > MAX_INPUT_DIMENSION) {
        throw new ImagePipelineError('tooLarge')
      }
      const scale = Math.min(1, MAX_OUTPUT_DIMENSION / Math.max(dims.w, dims.h))
      if (scale < 1) {
        try {
          return await createImageBitmap(file, {
            resizeWidth: Math.max(1, Math.round(dims.w * scale)),
            resizeHeight: Math.max(1, Math.round(dims.h * scale)),
            resizeQuality: 'high',
          })
        } catch {
          /* older browser without resize options — fall through to a full decode */
        }
      }
    }
    try {
      return await createImageBitmap(file)
    } catch {
      /* fall through to the <img> path */
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new ImagePipelineError('decodeFailed'))
    }
    img.src = url
  })
}

function dimensions(source: Decoded): { width: number; height: number } {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight }
  }
  return { width: source.width, height: source.height }
}

/**
 * Stepwise halving to the target longest edge. Halving repeatedly and drawing
 * each intermediate keeps every single drawImage cheap and the result smoother
 * than one big downscale (browsers do a poor job of large single-step
 * reductions).
 */
function downscale(source: Decoded | HTMLCanvasElement, maxEdge: number): HTMLCanvasElement {
  const start =
    source instanceof HTMLCanvasElement
      ? { width: source.width, height: source.height }
      : dimensions(source)

  const scale = Math.min(1, maxEdge / Math.max(start.width, start.height))
  const targetW = Math.max(1, Math.round(start.width * scale))
  const targetH = Math.max(1, Math.round(start.height * scale))

  let currentW = start.width
  let currentH = start.height
  let current: CanvasImageSource = source

  // Halve until within 2× of the target, then the final draw lands it exactly.
  while (currentW > targetW * 2 && currentH > targetH * 2) {
    const halfW = Math.max(targetW, Math.round(currentW / 2))
    const halfH = Math.max(targetH, Math.round(currentH / 2))
    current = paint(current, currentW, currentH, halfW, halfH)
    currentW = halfW
    currentH = halfH
  }

  return paint(current, currentW, currentH, targetW, targetH)
}

function paint(
  source: CanvasImageSource,
  _fromW: number,
  _fromH: number,
  toW: number,
  toH: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = toW
  canvas.height = toH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new ImagePipelineError('decodeFailed')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, 0, 0, toW, toH)
  return canvas
}

function toJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ImagePipelineError('decodeFailed'))),
      'image/jpeg',
      quality,
    )
  })
}

/** The name is display-only metadata; keep it short and strip path separators. */
function sanitizeName(name: string): string {
  return (name.split(/[\\/]/).pop() || 'image.jpg').slice(0, 120)
}
